import React, {useState} from 'react';
import Icon from '@/shared/components/Icon';
import clsx from 'clsx';
import {toast} from 'react-toastify';
import type {OrganizationSearchResult} from '@/shared/types/api';
import {readOhdOrgId} from '@/shared/constants/ohd';
import {AdminService} from '@/modules/admin/services/adminService';
import {useOhdOrganizations} from '../hooks/useOhdOrganizations';
import OhdFinderModal from './OhdFinderModal';
import OhdSyncButton from './OhdSyncButton';

interface Props {
    orgId: number;
    orgName: string;
    features?: Record<string, unknown> | null;
    allMldOrgs: OrganizationSearchResult[];
    onChanged: () => void;
}

/**
 * OHD / MDX dashboard-mode control for an organization.
 *
 * OHD mode is driven entirely by the OHD *link* (Approach B): linking an OHD
 * org via the finder enables OHD mode, and unlinking returns the org to MDX
 * mode. There is deliberately no way to enable OHD mode without a link — the
 * `enable_employee_health_dashboard` flag is only ever set as a side effect of
 * linking (see OhdFinderModal), and the API enforces the same invariant
 * server-side, so the previous "OHD on but Not linked" state is impossible.
 *
 * The two pills are the only controls: clicking "OHD Dashboard" links (or
 * relinks) via the finder, and clicking "MDX Dashboard" unlinks. There are no
 * separate Find/Relink/Unlink buttons — they would duplicate the pills.
 */
const pillClass = (active: boolean) =>
    clsx(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition',
        active
            ? 'bg-mld-success/5 text-mld-success hover:bg-mld-success/10'
            : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border'
    );

const OhdModeControl: React.FC<Props> = ({orgId, orgName, features, allMldOrgs, onChanged}) => {
    const [finderOpen, setFinderOpen] = useState(false);
    const [confirmUnlink, setConfirmUnlink] = useState(false);
    const [unlinking, setUnlinking] = useState(false);

    const ohdOrgId = readOhdOrgId((features ?? undefined) as {ohd_organization_id?: number | string | null} | undefined);
    const isLinked = typeof ohdOrgId === 'number';
    const storedOhdOrgName =
        typeof features?.ohd_organization_name === 'string' ? (features.ohd_organization_name as string) : undefined;

    // Legacy links stored only the id — a bare "#684" is how the wrong-org
    // mis-pick went unnoticed. Resolve the name from the live OHD list so the
    // link target is always shown by name; the query only fires for that gap.
    const ohdOrgsQuery = useOhdOrganizations(isLinked && !storedOhdOrgName);
    const ohdOrgName =
        storedOhdOrgName ??
        (isLinked ? ohdOrgsQuery.data?.find((o) => o.organization_id === ohdOrgId)?.organization_name : undefined);

    async function handleUnlink() {
        try {
            setUnlinking(true);
            await AdminService.updateOrganizationFeatures(orgId, {
                ohd_organization_id: null,
                ohd_organization_name: null,
                enable_employee_health_dashboard: false,
            });
            onChanged();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to unlink OHD organization');
        } finally {
            setUnlinking(false);
            setConfirmUnlink(false);
        }
    }

    return (
        <div className="ml-4 flex flex-wrap items-center gap-2">
            {/* Mode pills — the single control: OHD = link/relink, MDX = unlink */}
            <div className="flex items-center gap-1 rounded-full border border-mld-panel-border bg-mld-background p-0.5">
                <button
                    type="button"
                    onClick={() => setFinderOpen(true)}
                    title={isLinked ? 'Change the linked OHD organization' : 'Link an OHD organization to enable OHD mode'}
                    className={pillClass(isLinked)}>
                    OHD Dashboard
                </button>
                <button
                    type="button"
                    onClick={() => {
                        if (isLinked) setConfirmUnlink(true);
                    }}
                    title={isLinked ? 'Unlink and switch back to MDX' : undefined}
                    className={pillClass(!isLinked)}>
                    MDX Dashboard
                </button>
            </div>

            {confirmUnlink ? (
                <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1">
                    <span className="text-[11px] text-amber-800">
                        Unlink{ohdOrgName ? ` from ${ohdOrgName}` : ''} and switch back to MDX?
                    </span>
                    <button
                        type="button"
                        disabled={unlinking}
                        onClick={handleUnlink}
                        className="inline-flex items-center gap-1 rounded bg-mld-menu-text px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-mld-menu-text/90 disabled:opacity-60">
                        {unlinking && <Icon name="spinner" spin className="h-3 w-3" />}
                        Unlink
                    </button>
                    <button
                        type="button"
                        disabled={unlinking}
                        onClick={() => setConfirmUnlink(false)}
                        className="rounded px-2 py-0.5 text-[11px] font-semibold text-mld-text-secondary hover:bg-mld-menu-background">
                        Cancel
                    </button>
                </div>
            ) : isLinked ? (
                <>
                    <div className="flex items-center gap-1.5 rounded-md border border-mld-panel-border bg-white px-2 py-1">
                        <Icon name="link" className="h-3 w-3 text-emerald-600" />
                        {ohdOrgName && <span className="text-[11px] font-medium text-mld-menu-text">{ohdOrgName}</span>}
                        <span className="text-[10px] text-mld-muted-text">#{ohdOrgId}</span>
                    </div>
                    <OhdSyncButton orgId={orgId} />
                </>
            ) : (
                <span className="text-[11px] text-mld-muted-text">Not linked</span>
            )}

            <OhdFinderModal
                open={finderOpen}
                onClose={() => setFinderOpen(false)}
                mldOrgId={orgId}
                mldOrgName={orgName}
                currentOhdOrgId={ohdOrgId}
                currentOhdOrgName={ohdOrgName}
                allMldOrgs={allMldOrgs}
                onMapped={onChanged}
            />
        </div>
    );
};

export default OhdModeControl;
